import { useEffect, useRef, useState } from "react";
import { Box, Divider, Typography } from "@mui/material";

import "./TermsAndConditions.css";

const sections = [
  { id: "acceptance", label: "Acceptance of Terms" },
  { id: "use-of-service", label: "Use of the Service" },
  { id: "user-accounts", label: "User Accounts" },
  { id: "intellectual-property", label: "Intellectual Property" },
  { id: "prohibited-conduct", label: "Prohibited Conduct" },
  { id: "payments", label: "Fees" },
  { id: "termination", label: "Termination" },
  { id: "disclaimers", label: "Disclaimers" },
  { id: "limitation", label: "Limitation of Liability" },
  { id: "governing-law", label: "Governing Law" },
  { id: "changes", label: "Changes to Terms" },
  { id: "contact", label: "Contact Us" },
];

export function TermsOfService() {
  const [activeSection, setActiveSection] = useState("acceptance");
  const sectionRefs = useRef({});

  useEffect(() => {
    const scroller = document.querySelector(".legalLayoutRoot");

    const handleScroll = () => {
      const OFFSET = 120;
      let currentSection = sections[0].id;
      for (const { id } of sections) {
        const el = sectionRefs.current[id];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= OFFSET) {
            currentSection = id;
          }
        }
      }
      setActiveSection(currentSection);
    };

    const target = scroller || window;
    target.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => target.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const setRef = (id) => (el) => {
    sectionRefs.current[id] = el;
  };

  return (
    <Box className="legalPageWrap legalTermsWrap">
      <Box component="aside" className="legalSidebar">
        <Box component="nav">
          <Box component="ul" className="legalSidebarList">
            {sections.map(({ id, label }) => {
              const isActive = activeSection === id;
              return (
                <Box component="li" key={id} className="legalSidebarItem">
                  <button
                    onClick={() => scrollToSection(id)}
                    className={`legalSidebarBtn ${isActive ? "isActive" : ""}`}
                    type="button"
                  >
                    {label}
                  </button>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box component="main" className="legalMain">
        <Typography className="legalH1" component="h1">
          Terms of Service
        </Typography>
        <Typography className="legalEffective" component="p">
          Effective: 14 October 2026
        </Typography>

        <Box className="legalInfoBanner legalInfoBanner--blue">
          <Box className="legalInfoBannerStripe legalInfoBannerStripe--blue" />
          <Typography
            className="legalInfoBannerText legalInfoBannerText--blue"
            component="p"
          >
            Please read these Terms of Service carefully before using The Quest.
            By signing in, you confirm that you are at least 16 years old and
            agree to be legally bound by these terms. If you do not agree,
            please do not use The Quest.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="acceptance"
          ref={setRef("acceptance")}
          className="legalSection"
        >
          <SectionTitle number={1} title="Acceptance of Terms" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            These Terms apply to your use of The Quest, a web platform for
            employee engagement and career development (the "Service"). The
            Quest is provided by QCG Sweden AB, Engelbrektsgatan 6, 211 33
            Malmö, Sweden ("Q Group", "we", "us"). Sliq Group Private Limited,
            India, provides engineering, hosting administration, and support to
            Q Group as its service provider.
          </Typography>
          <Typography className="legalBody legalMutedText legalBodyHasBottomSmall" component="p">
            Your organisation provisions your account. By signing in, you
            acknowledge that you have read and agree to these Terms and our
            Privacy Policy, which is incorporated herein by reference.
          </Typography>
          <Typography className="legalBody legalMutedText legalBodyHasBottomSmall" component="p">
            If your organisation has a separate agreement with Q Group, that
            agreement prevails where they conflict, including fees, uptime,
            liability, notice, and any transition period.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            Q Group uses the suppliers named in the Privacy Policy, including
            Sliq Group in India, as its service providers.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="use-of-service"
          ref={setRef("use-of-service")}
          className="legalSection"
        >
          <SectionTitle number={2} title="Use of the Service" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            Q Group grants you a limited, non-exclusive, non-transferable,
            revocable licence to access and use the Service as an employee of a
            customer organisation, subject to these Terms.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            You agree to use the Service only for lawful purposes and in
            accordance with these Terms. You are responsible for all activities
            that occur under your account and for maintaining the
            confidentiality of your login credentials.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="user-accounts"
          ref={setRef("user-accounts")}
          className="legalSection"
        >
          <SectionTitle number={3} title="User Accounts" />
          <Typography className="legalBody legalBodyBlack" component="p">
            Your organisation creates your account. You agree to:
          </Typography>
          <Box
            component="ul"
            className="legalList legalListTight legalMutedText legalListHasBottom"
          >
            <li>
              Keep the profile details you can edit accurate, including your
              name, date of birth, and mobile number
            </li>
            <li>
              Keep your password secure and not share it with any third party
            </li>
            <li>
              Notify us immediately at{" "}
              <a href="mailto:info@quest-q.com" className="legalLink">
                info@quest-q.com
              </a>{" "}
              if you suspect any unauthorised use of your account
            </li>
            <li>
              Accept responsibility for all activities that occur under your
              account
            </li>
          </Box>
          <Typography className="legalBody legalMutedText" component="p">
            Q Group or your organisation may suspend accounts that contain false
            or misleading information.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="intellectual-property"
          ref={setRef("intellectual-property")}
          className="legalSection"
        >
          <SectionTitle number={4} title="Intellectual Property" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            The Service and all content, features, and functionality — including
            but not limited to text, graphics, logos, icons, software, and
            design — are owned by Q Group or its licensors and are protected by
            applicable intellectual property laws.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            You retain ownership of any content you submit to the Service ("User
            Content"). By submitting User Content, you grant Q Group and its
            service providers acting for it a non-exclusive licence to store
            and display that content solely to provide the Service for your
            organisation.{" "}
            <span className="legalEmphasis">
              You may not copy, modify, distribute, sell, or lease any part of
              our Service or its content without our prior written consent.
            </span>
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="prohibited-conduct"
          ref={setRef("prohibited-conduct")}
          className="legalSection"
        >
          <SectionTitle number={5} title="Prohibited Conduct" />
          <Typography className="legalBody legalBodyBlack" component="p">
            When using The Quest, you agree not to:
          </Typography>
          <Box
            component="ul"
            className="legalList legalListTight legalMutedText"
          >
            <li>
              Violate any applicable local, national, or international law or
              regulation
            </li>
            <li>
              Attempt to gain unauthorised access to any part of the Service or
              its related systems
            </li>
            <li>
              Transmit any harmful, offensive, defamatory, or otherwise
              objectionable content
            </li>
            <li>
              Reverse engineer, decompile, or disassemble any portion of the
              Service
            </li>
            <li>
              Use automated tools, bots, or scrapers to extract data without our
              prior consent
            </li>
            <li>
              Interfere with or disrupt the integrity or performance of the
              Service
            </li>
            <li>Use the Service to send unsolicited communications or spam</li>
            <li>
              Impersonate any person or entity, or misrepresent your affiliation
              with a person or entity
            </li>
          </Box>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="payments"
          ref={setRef("payments")}
          className="legalSection"
        >
          <SectionTitle number={6} title="Fees" />
          <Typography className="legalBody legalMutedText" component="p">
            Fees for The Quest are agreed between Q Group and your organisation.
            Individual users are not charged in the Service, and The Quest does
            not show a pricing or billing page.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="termination"
          ref={setRef("termination")}
          className="legalSection"
        >
          <SectionTitle number={7} title="Termination" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            You cannot close your own account in settings. An administrator in
            your organisation can end your access and request erasure of your
            personal data. Q Group may suspend access immediately if we
            reasonably believe you have violated these Terms, posed a security
            risk, or engaged in fraudulent or harmful conduct. Ending the
            organisation's service follows the customer agreement.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            When access ends, your right to use the Service ceases. Provisions
            of these Terms that by their nature should survive termination shall
            remain in effect.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="disclaimers"
          ref={setRef("disclaimers")}
          className="legalSection"
        >
          <SectionTitle number={8} title="Disclaimers" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            For individual use, the Service is provided on an "as is" and "as
            available" basis, without warranties of any kind, either express or
            implied. Q Group does not warrant to you that the Service will be
            uninterrupted, error-free, or free of viruses or other harmful
            components. Organisation uptime commitments, if any, are only in the
            customer agreement.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            To the fullest extent permitted by applicable law, we disclaim all
            warranties, including implied warranties of merchantability, fitness
            for a particular purpose, and non-infringement.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="limitation"
          ref={setRef("limitation")}
          className="legalSection"
        >
          <SectionTitle number={9} title="Limitation of Liability" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            To the maximum extent permitted by law, Q Group, its service
            providers (including Sliq Group), and their officers, directors,
            employees, and agents shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages including
            loss of profits, data, goodwill, or business opportunities arising
            from your use of or inability to use the Service.
          </Typography>
          <Typography className="legalBody legalMutedText legalBodyHasBottomSmall" component="p">
            In no event shall our aggregate liability to you for all claims
            arising out of or related to the Service exceed the total amount
            your organisation paid to Q Group in the twelve months preceding the
            event giving rise to the claim.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            Nothing in these Terms limits liability that cannot be limited by
            law.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="governing-law"
          ref={setRef("governing-law")}
          className="legalSection"
        >
          <SectionTitle number={10} title="Governing Law" />
          <Typography className="legalBody legalMutedText" component="p">
            These Terms are governed by the laws of Sweden. Disputes arising out
            of these Terms or the Service are subject to the courts of Sweden.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="changes"
          ref={setRef("changes")}
          className="legalSection"
        >
          <SectionTitle number={11} title="Changes to Terms" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            Q Group may revise these Terms from time to time. Material changes
            will be posted in the Service before they take effect.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            Your continued use of The Quest after the effective date of revised
            Terms constitutes your acceptance of those changes. If you do not
            agree, you must stop using the Service. Your organisation
            administrator controls whether your account remains active.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="contact"
          ref={setRef("contact")}
          className="legalSection legalSectionLast"
        >
          <SectionTitle number={12} title="Contact Us" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottom"
            component="p"
          >
            Questions about these Terms:
          </Typography>
          <Box className="legalContact legalMutedText">
            <Typography component="p" className="legalBody">
              <span className="legalEmphasis">Email:</span>{" "}
              <a href="mailto:info@quest-q.com" className="legalLink">
                info@quest-q.com
              </a>
            </Typography>
            <Typography component="p" className="legalBody legalContactCompany">
              <span className="legalEmphasis">Provider:</span> QCG Sweden AB,
              Engelbrektsgatan 6, 211 33 Malmö, Sweden
            </Typography>
            <Typography component="p" className="legalBody legalContactCompany">
              <span className="legalEmphasis">Service provider:</span> Sliq
              Group Private Limited, India
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function SectionTitle({ number, title }) {
  return (
    <Typography component="h2" className="legalSectionTitle">
      {number}. {title}
    </Typography>
  );
}
